import { useEffect, useState, useCallback } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3, Users, FileText, MessageSquare, Search, Trash2, ShieldCheck,
  ShieldOff, X, Phone, Mail, MapPin, Package, RefreshCw, Bot, Ban,
} from "lucide-react";
import { API } from "@/lib/api";
import { useAuth, getToken } from "@/lib/auth";
import ExportButton from "@/components/admin/ExportButton";
import LeadsTab from "@/components/admin/LeadsTab";
import OptOutsTab from "@/components/admin/OptOutsTab";

const tabs = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "suppliers", label: "Suppliers", icon: Users },
  { id: "leads", label: "Leads", icon: Bot },
  { id: "quotes", label: "Quotes", icon: FileText },
  { id: "searches", label: "Searches", icon: MessageSquare },
  { id: "optouts", label: "Opt-outs", icon: Ban },
];

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function AdminDashboardPage() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");

  if (loading) {
    return <div className="max-w-7xl mx-auto px-6 py-12 text-sm text-neutral-500 font-mono-tech">[ Loading... ]</div>;
  }
  if (!user) return <Navigate to="/login" replace state={{ from: "/admin" }} />;
  if (user.role !== "admin") {
    return (
      <div className="max-w-md mx-auto px-6 py-16 text-center" data-testid="admin-forbidden">
        <h1 className="font-display text-3xl font-extrabold mb-2">Access denied</h1>
        <p className="text-sm text-neutral-500">Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-mono-tech tracking-[0.25em] text-[#FF5100]">OWNER · SPANNR ADMIN</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold leading-none mt-1">
            Platform overview
          </h1>
        </div>
        <div className="text-xs text-neutral-500 font-mono-tech">
          Signed in as <span className="text-neutral-900">{user.email}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-neutral-200 flex flex-wrap gap-1 mb-6">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider font-bold transition-colors -mb-px border-b-2 ${
                active
                  ? "border-[#FF5100] text-[#FF5100]"
                  : "border-transparent text-neutral-500 hover:text-neutral-900"
              }`}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <Overview />}
      {tab === "suppliers" && <Suppliers />}
      {tab === "leads" && <LeadsTab />}
      {tab === "quotes" && <Quotes />}
      {tab === "searches" && <Searches />}
      {tab === "optouts" && <OptOutsTab />}
    </div>
  );
}

/* ---------------- Overview ---------------- */
function Overview() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reminderBusy, setReminderBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/stats`, auth());
      setData(r.data);
    } catch {
      toast.error("Failed to load stats");
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runReminders = async () => {
    if (!window.confirm("Send trial-reminder emails now? Only suppliers in the 3d/1d/0d windows will receive one (each is sent at most once).")) return;
    setReminderBusy(true);
    try {
      const r = await axios.post(`${API}/admin/trial-reminders/run`, {}, auth());
      const total = (r.data["3d"] || 0) + (r.data["1d"] || 0) + (r.data["0d"] || 0);
      const wa = r.data["wa_sent"] || 0;
      toast.success(`Reminders sent: ${total} email (3d:${r.data["3d"]}, 1d:${r.data["1d"]}, 0d:${r.data["0d"]})${wa ? ` + ${wa} WhatsApp` : ""}`);
    } catch {
      toast.error("Could not run reminders");
    } finally {
      setReminderBusy(false);
    }
  };

  const runSearchResume = async () => {
    if (!window.confirm("Run WhatsApp search-resume job? Sends spannr_search_resume template to WA buyers whose search is 48h-7d old with no follow-up. Each search is resumed at most once. Requires TWILIO_TEMPLATE_SEARCH_RESUME env var to be set.")) return;
    setReminderBusy(true);
    try {
      const r = await axios.post(`${API}/admin/search-resume/run`, {}, auth());
      if (r.data.skipped_no_template === -1) {
        toast.warning("Template SID not set — waiting on Meta approval. No messages sent.");
      } else {
        toast.success(`Search-resume: ${r.data.resumed} sent · ${r.data.scanned} scanned · ${r.data.skipped_replied} replied already`);
      }
    } catch {
      toast.error("Could not run search-resume");
    } finally {
      setReminderBusy(false);
    }
  };

  if (!data) return <div className="text-sm text-neutral-500 font-mono-tech">[ Loading stats... ]</div>;

  const t = data.totals;
  const r7 = data.last_7_days;
  const subs = data.subscriptions || {};

  return (
    <div className="space-y-8" data-testid="admin-overview">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">All-time totals</div>
        <button onClick={load} disabled={busy} className="text-xs flex items-center gap-1.5 text-neutral-500 hover:text-[#FF5100]">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Suppliers" value={t.suppliers} sub={`${t.verified_suppliers} verified`} />
        <Stat label="Parts" value={t.parts} sub={`${t.in_stock_parts} in stock`} />
        <Stat label="Quotes" value={t.quotes} sub="all time" />
        <Stat label="Searches" value={t.chat_messages} sub={`${t.whatsapp_messages} via WA`} />
      </div>

      {/* Subscription health */}
      <div data-testid="admin-subs-health">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">
            Subscriptions & revenue
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runReminders}
              disabled={reminderBusy}
              data-testid="admin-run-reminders"
              className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 px-2 py-1 border border-neutral-200 hover:border-[#FF5100] hover:text-[#FF5100] transition-colors"
            >
              {reminderBusy ? "Sending..." : "Run trial reminders now"}
            </button>
            <button
              onClick={runSearchResume}
              disabled={reminderBusy}
              data-testid="admin-run-search-resume"
              className="text-[10px] uppercase tracking-wider font-bold flex items-center gap-1.5 px-2 py-1 border border-neutral-200 hover:border-[#FF5100] hover:text-[#FF5100] transition-colors"
            >
              {reminderBusy ? "Sending..." : "Run WA search-resume now"}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat label="MRR (paying)" value={`R${Math.round(subs.mrr_zar || 0).toLocaleString()}`} sub={`${subs.active || 0} active`} />
          <Stat label="In trial" value={subs.trial || 0} sub={`${subs.trials_expiring_soon || 0} ending in 7d`} />
          <Stat label="Past due" value={subs.past_due || 0} sub="needs attention" />
          <Stat label="Cancelled" value={subs.cancelled || 0} sub="all time" />
        </div>
        {subs.paying_by_tier && Object.keys(subs.paying_by_tier).length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {Object.entries(subs.paying_by_tier).map(([tier, n]) => (
              <span key={tier} className="px-2 py-1 border border-neutral-200 bg-neutral-50">
                <span className="capitalize font-bold">{tier}</span>
                <span className="text-neutral-500 ml-1.5 font-mono-tech">{n}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-3">
          Last 7 days
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <Stat label="New suppliers" value={r7.new_suppliers} />
          <Stat label="Quotes" value={r7.quotes} />
          <Stat label="Chat / WhatsApp" value={r7.chat_messages} />
          <Stat label="Payments" value={r7.payments_complete || 0} />
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-3">
          Top vehicle makes searched
        </div>
        <div className="space-y-1.5" data-testid="admin-top-makes">
          {data.top_makes_searched.length === 0 && (
            <div className="text-sm text-neutral-500">No search data yet.</div>
          )}
          {data.top_makes_searched.map((m, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-32 text-sm font-bold capitalize">{m.make || "(unknown)"}</div>
              <div className="flex-1 h-3 bg-neutral-100">
                <div
                  className="h-full bg-[#FF5100]"
                  style={{ width: `${Math.min(100, (m.count / data.top_makes_searched[0].count) * 100)}%` }}
                />
              </div>
              <div className="w-12 text-right text-sm font-mono-tech text-neutral-500">{m.count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="border border-neutral-200 p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">{label}</div>
      <div className="font-display text-3xl font-extrabold mt-1 leading-none">{value}</div>
      {sub && <div className="text-[11px] text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

/* ---------------- Suppliers ---------------- */
function Suppliers() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [filterVerified, setFilterVerified] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterSubStatus, setFilterSubStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (filterVerified) params.verified = filterVerified;
      if (filterType) params.type = filterType;
      if (filterSubStatus) params.sub_status = filterSubStatus;
      const r = await axios.get(`${API}/admin/suppliers`, { ...auth(), params });
      setList(r.data);
    } catch {
      toast.error("Failed to load suppliers");
    } finally {
      setBusy(false);
    }
  }, [q, filterVerified, filterType, filterSubStatus]);

  useEffect(() => { load(); }, [load]);

  const toggleVerify = async (s) => {
    try {
      await axios.patch(`${API}/admin/suppliers/${s.id}`, { verified: !s.verified }, auth());
      toast.success(s.verified ? "Unverified" : "Verified");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete supplier "${s.name}"? This will delete all their parts and the linked owner account.`)) return;
    try {
      await axios.delete(`${API}/admin/suppliers/${s.id}`, auth());
      toast.success("Supplier deleted");
      setSelectedId(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-suppliers">
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
          <input
            data-testid="admin-suppliers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, city, phone..."
            className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none pl-8 pr-3 h-9 text-sm"
          />
        </div>
        <select
          value={filterVerified}
          onChange={(e) => setFilterVerified(e.target.value)}
          className="bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-2 h-9 text-xs"
        >
          <option value="">All status</option>
          <option value="true">Verified</option>
          <option value="false">Unverified</option>
        </select>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-2 h-9 text-xs"
        >
          <option value="">All types</option>
          <option value="scrapyard">Scrapyard</option>
          <option value="dealership">Dealership</option>
          <option value="aftermarket">Aftermarket</option>
          <option value="workshop">Workshop</option>
          <option value="accessories">Vehicle Accessories</option>
          <option value="interior">Interior</option>
          <option value="wheels_tyres">Wheels & Tyres</option>
          <option value="offroad">4x4 & Off-Road</option>
          <option value="glass">Glass & Visibility</option>
        </select>
        <select
          value={filterSubStatus}
          onChange={(e) => setFilterSubStatus(e.target.value)}
          data-testid="admin-suppliers-sub-filter"
          className="bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-2 h-9 text-xs"
        >
          <option value="">All subscriptions</option>
          <option value="trial">Trial</option>
          <option value="active">Active (paying)</option>
          <option value="past_due">Past due</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-2 flex items-center justify-between">
        <span>{busy ? "Loading..." : `${list.length} suppliers`}</span>
        <ExportButton
          kind="suppliers"
          buildUrl={(cols) => {
            const p = new URLSearchParams();
            if (q.trim()) p.set("q", q.trim());
            if (filterVerified) p.set("verified", filterVerified);
            if (filterType) p.set("type", filterType);
            if (filterSubStatus) p.set("sub_status", filterSubStatus);
            if (cols) p.set("columns", cols);
            return `${API}/admin/export/suppliers.csv?${p.toString()}`;
          }}
        />
      </div>

      <div className="border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2.5 font-mono-tech">Name</th>
              <th className="text-left px-3 py-2.5 font-mono-tech hidden md:table-cell">Type</th>
              <th className="text-left px-3 py-2.5 font-mono-tech hidden md:table-cell">City</th>
              <th className="text-left px-3 py-2.5 font-mono-tech hidden lg:table-cell">Owner email</th>
              <th className="text-left px-3 py-2.5 font-mono-tech">Subscription</th>
              <th className="text-right px-3 py-2.5 font-mono-tech">Parts</th>
              <th className="text-right px-3 py-2.5 font-mono-tech hidden sm:table-cell">Quotes</th>
              <th className="text-center px-3 py-2.5 font-mono-tech">Verified</th>
              <th className="text-right px-3 py-2.5 font-mono-tech">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((s) => (
              <tr
                key={s.id}
                data-testid={`admin-supplier-row-${s.id}`}
                className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
                onClick={() => setSelectedId(s.id)}
              >
                <td className="px-3 py-2.5 font-medium">
                  {s.name}
                  <div className="text-[11px] text-neutral-500 md:hidden">
                    {s.type} · {s.city}
                  </div>
                </td>
                <td className="px-3 py-2.5 capitalize hidden md:table-cell">{s.type}</td>
                <td className="px-3 py-2.5 hidden md:table-cell">{s.city}</td>
                <td className="px-3 py-2.5 hidden lg:table-cell text-neutral-600 truncate max-w-[180px]">
                  {s.owner?.email || s.email || "—"}
                </td>
                <td className="px-3 py-2.5">
                  <SubBadge sub={s.subscription} />
                </td>
                <td className="px-3 py-2.5 text-right font-mono-tech">
                  {s.parts_count}<span className="text-neutral-400 text-[11px]"> ({s.in_stock_count})</span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono-tech hidden sm:table-cell">{s.quotes_count}</td>
                <td className="px-3 py-2.5 text-center">
                  {s.verified ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
                      <ShieldCheck size={12} /> Yes
                    </span>
                  ) : (
                    <span className="text-[11px] text-neutral-400 uppercase tracking-wider">No</span>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <div className="inline-flex gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleVerify(s); }}
                      data-testid={`admin-verify-toggle-${s.id}`}
                      title={s.verified ? "Unverify" : "Verify"}
                      className="p-1.5 text-neutral-500 hover:text-emerald-700 hover:bg-emerald-50"
                    >
                      {s.verified ? <ShieldOff size={14} /> : <ShieldCheck size={14} />}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); remove(s); }}
                      data-testid={`admin-delete-${s.id}`}
                      title="Delete"
                      className="p-1.5 text-neutral-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {list.length === 0 && !busy && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-neutral-500">No suppliers found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedId && (
        <SupplierDrawer
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function SupplierDrawer({ id, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/suppliers/${id}`, auth());
      setData(r.data);
    } catch {
      toast.error("Failed to load supplier");
      onClose();
    } finally {
      setBusy(false);
    }
  }, [id, onClose]);
  useEffect(() => { load(); }, [load]);

  const toggleVerify = async () => {
    try {
      await axios.patch(`${API}/admin/suppliers/${id}`, { verified: !data.supplier.verified }, auth());
      toast.success(data.supplier.verified ? "Unverified" : "Verified");
      load();
      onChanged?.();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="admin-supplier-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-xl bg-white border-l border-neutral-200 overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-neutral-200 px-5 py-3 flex items-center justify-between">
          <div className="text-xs font-mono-tech tracking-[0.2em] text-[#FF5100]">SUPPLIER · DETAIL</div>
          <button onClick={onClose} data-testid="admin-drawer-close" className="p-1.5 text-neutral-500 hover:text-neutral-900">
            <X size={18} />
          </button>
        </div>

        {busy && <div className="p-6 text-sm text-neutral-500">Loading...</div>}
        {data && (
          <div className="p-5 space-y-6">
            <div>
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-display text-2xl font-extrabold leading-tight">{data.supplier.name}</h2>
                <button
                  onClick={toggleVerify}
                  data-testid="admin-drawer-verify"
                  className={`shrink-0 px-3 h-8 text-[11px] uppercase tracking-wider font-bold ${
                    data.supplier.verified
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-[#FF5100] text-white"
                  }`}
                >
                  {data.supplier.verified ? "Verified" : "Verify"}
                </button>
              </div>
              <div className="mt-1 text-xs text-neutral-500 capitalize">
                {data.supplier.type} · Rating {data.supplier.rating}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <Info icon={MapPin} label="City" value={data.supplier.city} />
              <Info icon={Phone} label="Phone" value={data.supplier.phone || "—"} />
              <Info icon={Mail} label="Email" value={data.supplier.email || "—"} />
              <Info icon={Package} label="Parts" value={`${data.parts.length} · ${data.parts.filter(p => p.in_stock).length} in stock`} />
            </div>

            {data.owner && (
              <div className="border border-neutral-200 p-3 bg-neutral-50">
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1">Owner account</div>
                <div className="text-sm font-medium">{data.owner.name}</div>
                <div className="text-xs text-neutral-500">{data.owner.email}</div>
              </div>
            )}

            {data.subscription && (
              <div className="border border-neutral-200 p-3" data-testid="admin-drawer-subscription">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">Subscription</div>
                  <SubBadge sub={{
                    status: data.subscription.effective_status,
                    tier: data.subscription.tier,
                    tier_name: data.subscription.tier_info?.name,
                    monthly_amount_zar: data.subscription.monthly_amount_zar,
                    trial_days_remaining: data.subscription.trial_days_remaining,
                    payfast_token: !!data.subscription.payfast_token,
                  }} />
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">Listings</div>
                    <div className="font-medium">
                      {data.subscription.listings_used}
                      {data.subscription.listings_limit !== null
                        ? ` / ${data.subscription.listings_limit}`
                        : " / ∞"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">Monthly</div>
                    <div className="font-medium">
                      {data.subscription.monthly_amount_zar
                        ? `R${data.subscription.monthly_amount_zar}`
                        : "—"}
                    </div>
                  </div>
                  {data.subscription.effective_status === "trial" && data.subscription.trial_end && (
                    <div className="col-span-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">Trial ends</div>
                      <div className="font-medium">{fmtDate(data.subscription.trial_end)}</div>
                    </div>
                  )}
                  {data.subscription.last_payment_at && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">Last payment</div>
                      <div className="font-medium">{fmtDate(data.subscription.last_payment_at)}</div>
                    </div>
                  )}
                  {data.subscription.current_period_end && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">Next charge</div>
                      <div className="font-medium">{fmtDate(data.subscription.current_period_end)}</div>
                    </div>
                  )}
                </div>
                {data.payments && data.payments.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-neutral-100">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">
                      Payments ({data.payments.length})
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {data.payments.map((p) => (
                        <div key={p.id} className="flex items-center justify-between text-xs">
                          <span className={p.status === "complete" ? "text-emerald-700" : "text-red-700"}>
                            {p.status === "complete" ? "✓" : "✗"} R{p.amount_zar}
                          </span>
                          <span className="text-neutral-400 font-mono-tech">{fmtDate(p.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual activation — for when PayFast charged but ITN didn't activate */}
                {["trial", "past_due"].includes(data.subscription.effective_status) && (
                  <div className="mt-3 pt-3 border-t border-neutral-100">
                    <ManualActivate
                      subId={data.subscription.id}
                      defaultAmount={data.subscription.monthly_amount_zar || 499}
                      onDone={() => { load(); onChanged?.(); }}
                    />
                  </div>
                )}

                {/* Custom deal — extend trial and/or grant unlimited listings */}
                <div className="mt-3 pt-3 border-t border-neutral-100">
                  <CustomDealOverride
                    subId={data.subscription.id}
                    listingsLimit={data.subscription.listings_limit}
                    trialEnd={data.subscription.trial_end}
                    overrides={data.subscription.overrides || []}
                    onDone={() => { load(); onChanged?.(); }}
                  />
                </div>
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-2">
                Inventory ({data.parts.length})
              </div>
              <div className="space-y-1.5 max-h-64 overflow-y-auto">
                {data.parts.length === 0 && <div className="text-sm text-neutral-500">No parts yet.</div>}
                {data.parts.map((p) => (
                  <div key={p.id} className="flex items-center justify-between gap-2 px-3 py-2 border border-neutral-100 text-sm">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{p.name}</div>
                      <div className="text-[11px] text-neutral-500 truncate">
                        {p.make} {p.model} · {p.engine} · {p.year_from}-{p.year_to}
                      </div>
                    </div>
                    <div className="text-right text-sm font-mono-tech shrink-0">
                      R{Math.round(p.price_zar).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-2">
                Recent quotes ({data.quotes.length})
              </div>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {data.quotes.length === 0 && <div className="text-sm text-neutral-500">No quotes yet.</div>}
                {data.quotes.map((q) => (
                  <div key={q.id} className="px-3 py-2 border border-neutral-100 text-sm">
                    <div className="font-medium">{q.buyer_name}</div>
                    <div className="text-[11px] text-neutral-500">
                      {q.buyer_phone || "no phone"} · {q.buyer_email || "no email"} · {fmtDate(q.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon size={14} className="mt-1 text-neutral-400" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500">{label}</div>
        <div className="font-medium truncate">{value}</div>
      </div>
    </div>
  );
}

function ManualActivate({ subId, defaultAmount, onDone }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(defaultAmount);
  const [pfPaymentId, setPfPaymentId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="admin-manual-activate-open"
        className="w-full text-left text-xs font-mono-tech tracking-wider uppercase border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 px-3 py-2 transition-colors"
      >
        ⚠ FORCE ACTIVATE (paid but stuck on trial)
      </button>
    );
  }

  async function submit() {
    if (!amount || amount <= 0) return toast.error("Amount required");
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/subscriptions/${subId}/activate`,
        {
          amount_zar: Number(amount),
          pf_payment_id: pfPaymentId.trim() || null,
          note: note.trim() || null,
        },
        auth(),
      );
      toast.success("Subscription activated");
      setOpen(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Activation failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="admin-manual-activate-form">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-amber-900 font-bold">
        FORCE ACTIVATE SUBSCRIPTION
      </div>
      <div className="text-[11px] text-amber-900">
        Use only when PayFast shows the customer as Active but our ITN didn&apos;t fire. Creates a payment record + flips status to active.
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase font-mono-tech text-neutral-500">Amount (R)</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            data-testid="admin-manual-activate-amount"
            className="w-full border border-neutral-300 px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase font-mono-tech text-neutral-500">PayFast Payment ID</label>
          <input
            type="text"
            value={pfPaymentId}
            onChange={(e) => setPfPaymentId(e.target.value)}
            placeholder="from PayFast UI"
            data-testid="admin-manual-activate-pf-id"
            className="w-full border border-neutral-300 px-2 py-1 text-sm font-mono"
          />
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase font-mono-tech text-neutral-500">Note (for audit)</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. ITN signature failed, manual activation"
          data-testid="admin-manual-activate-note"
          className="w-full border border-neutral-300 px-2 py-1 text-sm"
        />
      </div>
      <div className="flex gap-2 pt-1">
        <button
          onClick={submit}
          disabled={busy}
          data-testid="admin-manual-activate-submit"
          className="bg-[#FF5100] hover:bg-[#E64A00] text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5"
        >
          {busy ? "Activating..." : "Activate"}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="text-xs text-neutral-500 hover:text-neutral-900 px-3 py-1.5"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}



function CustomDealOverride({ subId, listingsLimit, trialEnd, overrides, onDone }) {
  const [open, setOpen] = useState(false);
  const [days, setDays] = useState("");
  const [unlimited, setUnlimited] = useState(listingsLimit === null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const isUnlimited = listingsLimit === null;
  const trialFmt = trialEnd ? new Date(trialEnd).toLocaleDateString() : "—";

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="admin-custom-deal-open"
        className="w-full text-left text-xs font-mono-tech tracking-wider uppercase border border-[#FF5100] bg-[#FFF6F0] hover:bg-[#FFE9D9] text-[#B03A00] px-3 py-2 transition-colors"
      >
        ✦ CUSTOM DEAL — extend trial / unlimited listings
      </button>
    );
  }

  async function submit() {
    const body = {};
    if (days && Number(days) > 0) body.extend_trial_days = Number(days);
    if (unlimited !== isUnlimited) body.unlimited_listings = unlimited;
    if (Object.keys(body).length === 0) {
      return toast.error("Nothing to change");
    }
    if (note.trim()) body.note = note.trim();

    setBusy(true);
    try {
      await axios.post(`${API}/admin/subscriptions/${subId}/override`, body, auth());
      toast.success("Custom deal applied");
      setOpen(false);
      setDays(""); setNote("");
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Override failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="border border-[#FF5100] bg-[#FFF6F0] p-3 space-y-3" data-testid="admin-custom-deal-form">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-[#B03A00] font-bold">
        CUSTOM DEAL
      </div>

      <div className="text-[11px] text-neutral-700 space-y-0.5">
        <div>Current trial end: <span className="font-mono">{trialFmt}</span></div>
        <div>Listings limit: <span className="font-mono">{isUnlimited ? "Unlimited" : listingsLimit ?? "—"}</span></div>
      </div>

      <div>
        <label className="text-[10px] uppercase font-mono-tech text-neutral-500 block mb-1">Extend trial by (days)</label>
        <div className="flex gap-1.5">
          <input
            type="number"
            value={days}
            onChange={(e) => setDays(e.target.value)}
            placeholder="0"
            data-testid="admin-custom-deal-days"
            className="flex-1 border border-neutral-300 px-2 py-1 text-sm"
          />
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(String(d))}
              data-testid={`admin-custom-deal-preset-${d}`}
              className="px-2 py-1 text-xs border border-neutral-300 hover:border-[#FF5100] hover:text-[#FF5100]"
            >
              +{d}d
            </button>
          ))}
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs cursor-pointer">
        <input
          type="checkbox"
          checked={unlimited}
          onChange={(e) => setUnlimited(e.target.checked)}
          data-testid="admin-custom-deal-unlimited"
          className="w-4 h-4 accent-[#FF5100]"
        />
        <span>Unlimited part listings (removes tier cap)</span>
      </label>

      <div>
        <label className="text-[10px] uppercase font-mono-tech text-neutral-500 block mb-1">Note (for audit)</label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Early adopter — 60d + unlimited"
          data-testid="admin-custom-deal-note"
          className="w-full border border-neutral-300 px-2 py-1 text-sm"
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={submit}
          disabled={busy}
          data-testid="admin-custom-deal-submit"
          className="bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-50 text-white text-xs font-bold uppercase tracking-wider px-3 py-1.5"
        >
          {busy ? "Applying..." : "Apply deal"}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="text-xs text-neutral-500 hover:text-neutral-900 px-3 py-1.5"
        >
          Cancel
        </button>
      </div>

      {overrides && overrides.length > 0 && (
        <div className="pt-2 border-t border-[#FFD4B8]">
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1">
            History ({overrides.length})
          </div>
          <div className="space-y-1 max-h-24 overflow-y-auto">
            {overrides.slice(-5).reverse().map((h, i) => (
              <div key={i} className="text-[11px] text-neutral-600 leading-tight">
                <span className="font-mono">{new Date(h.at).toLocaleDateString()}</span> — {h.note || "no note"}
                {h.extended_trial_days ? <span className="text-[#B03A00]"> · +{h.extended_trial_days}d trial</span> : null}
                {h.listings_limit ? <span className="text-[#B03A00]"> · listings: {h.listings_limit}</span> : null}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}



function SubBadge({ sub }) {
  if (!sub) {
    return <span className="text-[11px] text-neutral-400 uppercase tracking-wider">—</span>;
  }
  const colorMap = {
    trial: "bg-emerald-50 text-emerald-700 border-emerald-200",
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    past_due: "bg-red-50 text-red-700 border-red-200",
    cancelled: "bg-neutral-50 text-neutral-500 border-neutral-200",
  };
  const cls = colorMap[sub.status] || "bg-neutral-50 text-neutral-500 border-neutral-200";
  return (
    <div className="flex flex-col gap-0.5 min-w-[140px]">
      <div className="flex items-center gap-1.5">
        <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 border ${cls}`}>
          {sub.status === "past_due" ? "Past due" : sub.status}
        </span>
        <span className="text-[11px] capitalize font-bold">{sub.tier_name || sub.tier}</span>
      </div>
      <div className="text-[10px] text-neutral-500 font-mono-tech">
        {sub.status === "trial"
          ? `${sub.trial_days_remaining ?? 0}d left${sub.payfast_token ? " · card on file" : " · no card"}`
          : sub.status === "active"
          ? `R${sub.monthly_amount_zar}/mo`
          : sub.status === "past_due"
          ? "Needs payment"
          : "—"}
      </div>
    </div>
  );
}

/* ---------------- Quotes ---------------- */
function Quotes() {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(90);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/quotes`, { ...auth(), params: { limit: 200 } });
      setList(r.data);
    } catch {
      toast.error("Failed to load quotes");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-quotes">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-2 flex items-center justify-between gap-3 flex-wrap">
        <span>{busy ? "Loading..." : `${list.length} recent quotes`}</span>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-2 h-9 text-xs"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 12 months</option>
            <option value="0">All time</option>
          </select>
          <ExportButton
            kind="quotes"
            buildUrl={(cols) => {
              const p = new URLSearchParams();
              if (days) p.set("days", String(days));
              if (cols) p.set("columns", cols);
              return `${API}/admin/export/quotes.csv?${p.toString()}`;
            }}
          />
        </div>
      </div>
      <div className="border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2.5 font-mono-tech">Date</th>
              <th className="text-left px-3 py-2.5 font-mono-tech">Buyer</th>
              <th className="text-left px-3 py-2.5 font-mono-tech hidden md:table-cell">Part</th>
              <th className="text-left px-3 py-2.5 font-mono-tech hidden lg:table-cell">Supplier</th>
              <th className="text-right px-3 py-2.5 font-mono-tech hidden sm:table-cell">Price</th>
            </tr>
          </thead>
          <tbody>
            {list.map((q) => (
              <tr key={q.id} className="border-t border-neutral-100" data-testid={`admin-quote-row-${q.id}`}>
                <td className="px-3 py-2.5 text-xs text-neutral-500 font-mono-tech whitespace-nowrap">{fmtDate(q.created_at)}</td>
                <td className="px-3 py-2.5">
                  <div className="font-medium">{q.buyer_name}</div>
                  <div className="text-[11px] text-neutral-500">{q.buyer_phone || q.buyer_email || "—"}</div>
                </td>
                <td className="px-3 py-2.5 hidden md:table-cell">
                  {q.part ? <>
                    <div className="font-medium">{q.part.name}</div>
                    <div className="text-[11px] text-neutral-500">{q.part.make} {q.part.model} · {q.part.engine}</div>
                  </> : "—"}
                </td>
                <td className="px-3 py-2.5 hidden lg:table-cell">
                  {q.supplier ? <>
                    <div className="font-medium">{q.supplier.name}</div>
                    <div className="text-[11px] text-neutral-500">{q.supplier.city}</div>
                  </> : "—"}
                </td>
                <td className="px-3 py-2.5 text-right font-mono-tech hidden sm:table-cell">
                  {q.part ? `R${Math.round(q.part.price_zar).toLocaleString()}` : "—"}
                </td>
              </tr>
            ))}
            {list.length === 0 && !busy && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-neutral-500">No quotes yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- Searches ---------------- */
function Searches() {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);
  const [channel, setChannel] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/admin/searches`, { ...auth(), params: { limit: 200 } });
      setList(r.data);
    } catch {
      toast.error("Failed to load searches");
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-searches">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-2 flex items-center justify-between gap-3 flex-wrap">
        <span>{busy ? "Loading..." : `${list.length} recent searches`}</span>
        <div className="flex items-center gap-2">
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-2 h-9 text-xs"
          >
            <option value="">All channels</option>
            <option value="web">Web</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <select
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
            className="bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-2 h-9 text-xs"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 12 months</option>
            <option value="0">All time</option>
          </select>
          <ExportButton
            kind="searches"
            buildUrl={(cols) => {
              const p = new URLSearchParams();
              if (days) p.set("days", String(days));
              if (channel) p.set("channel", channel);
              if (cols) p.set("columns", cols);
              return `${API}/admin/export/searches.csv?${p.toString()}`;
            }}
          />
        </div>
      </div>
      <div className="space-y-2">
        {list.map((m) => (
          <div key={m.id} className="border border-neutral-200 p-3" data-testid={`admin-search-row-${m.id}`}>
            <div className="flex items-center justify-between gap-2 mb-1">
              <span className={`text-[10px] uppercase tracking-wider font-mono-tech ${
                m.channel === "whatsapp" ? "text-emerald-700" : "text-neutral-500"
              }`}>
                {m.channel === "whatsapp" ? "WhatsApp" : "Web"}
                {m.from_number && ` · ${m.from_number.replace("whatsapp:", "")}`}
              </span>
              <span className="text-[10px] text-neutral-400 font-mono-tech">{fmtDate(m.created_at)}</span>
            </div>
            <div className="text-sm font-medium">{m.user_text}</div>
            {m.extracted?.summary && (
              <div className="text-[11px] text-neutral-500 mt-0.5">→ {m.extracted.summary}</div>
            )}
            {Array.isArray(m.match_ids) && m.match_ids.length > 0 && (
              <div className="text-[11px] text-neutral-500 mt-0.5">{m.match_ids.length} match(es)</div>
            )}
          </div>
        ))}
        {list.length === 0 && !busy && (
          <div className="text-sm text-neutral-500 px-3 py-8 text-center border border-neutral-200">No searches yet.</div>
        )}
      </div>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-ZA", { dateStyle: "short", timeStyle: "short" });
  } catch { return iso; }
}
